import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { getEventRoutes } from '../infrastructure/utils/route.utils';
import { RouteParams, Routes } from '../routes-table';
import { IRouter } from '../infrastructure/router';
import { getMainNavigationLinks } from '../constants/navigation.constants';
import { hasPermission } from '../infrastructure/utils/has-permissions';
import { Permission } from '../api/roles.dao';
import { IAccountsDAO } from '../api/accounts.dao';
import { SelectGroupsVM } from './global/select-group/select-group.viewmodel';
import { SelectTimezonesVM } from './global/select-timezone/select-timezones.viewmodel';
import TrackingServiceProvider from '../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../tracking-service/tracking-values';

enum RootPages {
    Events,
    Zoom,
    EventMeetings,
    AttendeeDetails,
    MeetingsUploads,
    AttendeeForm,
    EventAttendees,
    Accounts,
    Operators,
    Users,
    Roles,
    Themes,
    AccountUsers,
    UserGroups,
    AccountAdmin,
    EventTracker,
    AccountGroups,
    Blueprints,
    EmailTemplates,
    EventHubs,
    Restricted,
    ManageAttributes,
    OeData,
}

export interface Route {
    path: Routes;
    params: {
        accountId?: string;
        eventId?: string;
    };
}

class GlobalViewModel {
    @mobx.observable
    private activePage: RootPages;
    @mobx.observable
    private fullScreen = false;
    private intl: IntlShape;

    @mobx.computed
    get isFullScreen(): boolean {
        return this.fullScreen;
    }

    constructor(
        @IoC.Inject private readonly router: IRouter<RouteParams>,
        @IoC.Inject private readonly selectTimezonesVM: SelectTimezonesVM,
        @IoC.Inject private readonly selectGroupVM: SelectGroupsVM,
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) {}

    public getTimezonesVM() {
        return this.selectTimezonesVM;
    }
    public getGroupsVM() {
        return this.selectGroupVM;
    }

    public getRootPage() {
        return this.activePage;
    }

    public setIntl = (intl: IntlShape) => {
        this.intl = intl;
    };

    public startListeningRouter = (customRoute?: Route) => {
        this.router.listen(Routes.Default, async () => {
            /* if user does not have accountList and eventTracker permissions, he should be redirected to event list */
            if (customRoute && ![
                Routes.Default,
                Routes.AccountsList,
                Routes.EventTrackerUpcoming,
                Routes.EventTrackerPast,
                Routes.EventTrackerDelivered
            ].includes(customRoute?.path)) {
                this.router.navigate(customRoute.path, customRoute.params);
                return;
            }

            const accountListPermission = hasPermission(Permission.ACCOUNTS_LIST);
            const eventTrackerPermission = hasPermission(Permission.EVENT_TRACKER);

            if (!accountListPermission && !eventTrackerPermission) {
                let firstUserAccount;

                try {
                    const response = await this.accountsDAO.getUserAccounts({
                        page: 0,
                        size: 1
                    });
                    if (response.content?.length) {
                        firstUserAccount = response.content[0];
                    }
                }
                // eslint-disable-next-line no-empty
                catch (err) { }

                if (firstUserAccount) {
                    this.router.navigate(Routes.EventsList, { accountId: firstUserAccount.accountId.toString() });
                } else {
                    const routesList = getMainNavigationLinks('', this.intl);
                    const routeEnum = routesList.find(route => !route.isHidden)?.routeEnum || Routes.AccountsList;

                    this.router.navigate(routeEnum, {});
                }
            } else {
                if (customRoute && customRoute.path !== Routes.Default) { // if a User clicks on Account List/Event Tracker landings URL
                    this.router.navigate(customRoute.path, {});
                    return;
                } else {
                    if (eventTrackerPermission) {
                        this.router.navigate(Routes.EventTrackerUpcoming, {});
                        return;
                    }
                    if (accountListPermission) {
                        this.router.navigate(Routes.AccountsList, {});
                        return;
                    }
                }
            }
        });

        this.router.listenAll(
            [Routes.AccountsList],
            () => {
                this.routeToRootPage(RootPages.Accounts);
                this.trackingService.get().track(TRACKING_EVENTS.ACCOUNTS_LIST_PAGE_LOAD, {});
            }
        );

        this.router.listenAll(
            [Routes.AccountGroupsList],
            () => this.routeToRootPage(RootPages.AccountGroups)
        );

        this.router.listenAll(
            getEventRoutes(),
            () => this.routeToRootPage(RootPages.Events)
        );

        this.router.listenAll(
            [Routes.AccountUsersList],
            () => this.routeToRootPage(RootPages.AccountUsers)
        );

        this.router.listenAll(
            [Routes.UserGroupsList],
            () => this.routeToRootPage(RootPages.UserGroups)
        );

        this.router.listenAll(
            [Routes.AccountAdmin, Routes.AccountAdminSettings, Routes.AccountAdminEventAttributes],
            () => this.routeToRootPage(RootPages.AccountAdmin)
        );
        this.router.listenAll(
            [Routes.EmailTemplates],
            () => this.routeToRootPage(RootPages.EmailTemplates)
        );
        this.router.listenAll(
            [Routes.EventHubList, Routes.EventHubAttendees, Routes.EventHubConfiguration, Routes.EventHubWebsiteBuilder],
            () => this.routeToRootPage(RootPages.EventHubs)
        );

        this.router.listenAll(
            [Routes.ZoomUsersManagement, Routes.TrackedZoomUsers, Routes.ZoomScheduledMeetings, Routes.TelephoneNumberSets,
            Routes.ZoomScheduledMeetingDetails, Routes.ZoomScheduleNewMeeting, Routes.ZoomUpdateMeeting,
            Routes.ZoomScheduledMeetingWebinarDetails],
            () => this.routeToRootPage(RootPages.Zoom)
        );

        this.router.listenAll(
            [Routes.OperatorsList],
            () => this.routeToRootPage(RootPages.Operators)
        );

        this.router.listenAll([Routes.UsersList], () => {
            this.routeToRootPage(RootPages.Users);
            this.trackingService.get().track(TRACKING_EVENTS.USERS_PAGE_LOAD, {});
        });

        this.router.listenAll(
            [Routes.RolesList],
            () => this.routeToRootPage(RootPages.Roles)
        );

        this.router.listenAll(
            [Routes.ThemesList],
            () => this.routeToRootPage(RootPages.Themes)
        );

        this.router.listenAll(
            [Routes.EventTrackerUpcoming, Routes.EventTrackerPast, Routes.EventTrackerDelivered],
            () => this.routeToRootPage(RootPages.EventTracker)
        );

        this.router.listen(Routes.OeDataOrganizations, () => this.routeToRootPage(RootPages.OeData));

        this.router.listenAll(
            [Routes.EventBlueprintList, Routes.MeetingTemplatesList, Routes.ConnectionTemplatesList, Routes.OrderTemplatesList],
            () => {
                this.selectGroupVM.pickGroups();
                this.selectTimezonesVM.getTimezonesList();
                this.routeToRootPage(RootPages.Blueprints);
            }
        );

        this.router.listenAll(
            [Routes.ManageEventAttributes],
            () => {
                this.routeToRootPage(RootPages.ManageAttributes);
            }
        );

        this.router.listenAll(
            [Routes.Restricted],
            () => this.routeToRootPage(RootPages.Restricted)
        );

        this.attachEventHubWebsiteBuilderListeners();

    };

    @mobx.action
    private routeToRootPage = (rootPage: RootPages) => {
        this.activePage = rootPage;
    };

    private attachEventHubWebsiteBuilderListeners() {
        this.router.listenAll([Routes.EventHubWebsiteBuilder], this.enableFullScreen);
        this.router.listenOthers(Routes.EventHubWebsiteBuilder, this.disableFullScreen);
    }

    @mobx.action.bound
    private enableFullScreen() {
        this.fullScreen = true;
    }

    @mobx.action.bound
    private disableFullScreen() {
        this.fullScreen = false;
    }
}

export { GlobalViewModel, RootPages };
